/**
 * category.service.ts
 * -------------------
 * Module 7 — AI-Based Guardrail Category Suggestion.
 *
 * Communicates with the backend category suggestion API:
 *   POST   /api/v1/sessions/{id}/categories/suggest          — trigger analysis
 *   GET    /api/v1/sessions/{id}/categories/suggestion-status — poll status
 *   GET    /api/v1/sessions/{id}/categories/suggestions       — fetch results
 *   POST   /api/v1/sessions/{id}/categories/suggestions/reset — re-trigger
 */

import { Injectable }                    from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError }        from 'rxjs';
import { catchError }                    from 'rxjs/operators';
import { environment }                   from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs — mirror backend Pydantic schemas
// ---------------------------------------------------------------------------

export interface SuggestResponse {
  session_id: string;
  message:    string;
}

export interface SuggestionStatusResponse {
  session_id: string;
  /** 'processing' | 'completed' | 'failed' | 'not_started' */
  status:   string;
  /** 0–100 */
  progress: number;
  message:  string;
  error:    string | null;
}

export interface ProofModel {
  file_name:       string;
  page_number:     number;
  referenced_text: string;
}

export interface SuggestedCategory {
  category_name:    string;
  summary:          string;
  /** 0–100 */
  confidence_score: number;
  proof:            ProofModel;
  /** User-editable in the UI (Module 8) */
  selected:         boolean;
}

export interface SuggestionsMetadata {
  total_categories_evaluated: number;
  categories_suggested:       number;
  generated_at:               string;
  model:                      string;
}

export interface SuggestionsResponse {
  session_id:           string;
  suggested_categories: SuggestedCategory[];
  metadata:             SuggestionsMetadata;
}

// ── Module 8 DTOs ───────────────────────────────────────────────────────────

/**
 * Response for GET /sessions/{id}/categories/available.
 * Returns all category names filtered by the session's app type(s).
 */
export interface AvailableCategoriesResponse {
  session_id:  string;
  categories:  string[];
  total:       number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class CategoryService {

  private readonly _base =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  constructor(private readonly http: HttpClient) {}

  // ── API calls ─────────────────────────────────────────────────────────────

  /**
   * Trigger the asynchronous LLM category analysis.
   * Returns 202 immediately — poll getSuggestionStatus() for progress.
   */
  triggerSuggestion(sessionId: string): Observable<SuggestResponse> {
    return this.http
      .post<SuggestResponse>(`${this._base}/${sessionId}/categories/suggest`, {})
      .pipe(catchError(this._handleError));
  }

  /**
   * Poll the current analysis status.
   * Call every 2–3 seconds until status === 'completed' or 'failed'.
   */
  getSuggestionStatus(sessionId: string): Observable<SuggestionStatusResponse> {
    return this.http
      .get<SuggestionStatusResponse>(
        `${this._base}/${sessionId}/categories/suggestion-status`
      )
      .pipe(catchError(this._handleError));
  }

  /**
   * Retrieve the completed suggestions once status === 'completed'.
   */
  getSuggestions(sessionId: string): Observable<SuggestionsResponse> {
    return this.http
      .get<SuggestionsResponse>(
        `${this._base}/${sessionId}/categories/suggestions`
      )
      .pipe(catchError(this._handleError));
  }

  /**
   * Reset the analysis so it can be re-triggered from scratch.
   */
  resetSuggestion(sessionId: string): Observable<{ session_id: string; message: string }> {
    return this.http
      .post<{ session_id: string; message: string }>(
        `${this._base}/${sessionId}/categories/suggestions/reset`,
        {}
      )
      .pipe(catchError(this._handleError));
  }

  /**
   * Module 8 — Fetch all guardrail category names applicable to the session's
   * application type(s).  Used to populate the "Available Categories" left panel.
   */
  getAvailableCategories(sessionId: string): Observable<AvailableCategoriesResponse> {
    return this.http
      .get<AvailableCategoriesResponse>(
        `${this._base}/${sessionId}/categories/available`
      )
      .pipe(catchError(this._handleError));
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private _handleError(err: HttpErrorResponse): Observable<never> {
    const message =
      err.error?.detail ??
      err.error?.message ??
      err.message ??
      'An unknown error occurred.';
    return throwError(() => new Error(message));
  }
}
